import React, { useState, useEffect } from 'react';
import { portsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Anchor, 
  Search,
  Loader2,
  MapPin,
  Globe,
  Filter
} from 'lucide-react';

const REGIONS = ['All', 'Asia', 'Europe', 'North America', 'South America', 'Middle East', 'Africa', 'Oceania', 'South Asia', 'Central America', 'Caribbean'];

function AdminPorts() {
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('All');

  useEffect(function() {
    loadPorts();
  }, []);

  async function loadPorts() {
    try {
      const response = await portsApi.getAll();
      setPorts(response.data);
    } catch (err) {
      console.error('Failed to load ports:', err);
    } finally {
      setLoading(false);
    }
  }

  const filteredPorts = ports.filter(function(port) {
    const matchesSearch = port.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      port.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      port.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = regionFilter === 'All' || port.region.includes(regionFilter);
    return matchesSearch && matchesRegion;
  });

  // Group ports by region for stats
  const regionCounts = {};
  ports.forEach(function(port) {
    const region = port.region;
    regionCounts[region] = (regionCounts[region] || 0) + 1;
  });

  return (
    <div className="space-y-6" data-testid="admin-ports">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Ports</h1>
          <p className="text-gray-600 mt-1">View and manage port database ({ports.length} ports)</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(regionCounts).slice(0, 6).map(function([region, count]) {
          return (
            <div key={region} className="bg-white rounded-sm shadow-card p-4">
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{count}</p>
              <p className="text-xs text-gray-500">{region}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-sm shadow-card p-4 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search ports by name, country, or code..."
            className="pl-12 h-12 rounded-sm"
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
          />
        </div>
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[200px] h-12 rounded-sm">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by region" />
          </SelectTrigger>
          <SelectContent>
            {REGIONS.map(function(region) {
              return (
                <SelectItem key={region} value={region}>{region}</SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        Showing <span className="font-medium text-ink">{filteredPorts.length}</span> of {ports.length} ports
      </p>

      {/* Ports Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : filteredPorts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPorts.map(function(port) {
            return (
              <div key={port.id} className="bg-white rounded-sm shadow-card p-5 transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                      <Anchor className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-ink">{port.name}</p>
                      <p className="text-xs text-gray-500">{port.country}</p>
                    </div>
                  </div>
                  <Badge className="bg-gray-100 text-gray-700 sv-mono text-xs">
                    {port.code}
                  </Badge>
                </div>
                
                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{port.region}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-500 text-xs">
                      {port.coordinates?.lat?.toFixed(4)}, {port.coordinates?.lng?.toFixed(4)}
                    </span>
                  </div>
                  {port.facilities && port.facilities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {port.facilities.slice(0, 3).map(function(facility, idx) {
                        return (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {facility}
                          </Badge>
                        );
                      })}
                      {port.facilities.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{port.facilities.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-card p-12 text-center">
          <Anchor className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No ports found matching your search</p>
        </div>
      )}
    </div>
  );
}

export default AdminPorts;
